<template>
  <div class="client-action-column" :data-test="`client-actions-${rowIndex}`">
    <ClientActionsMenu
      :data-test="`client-row-actions-${column.id}`"
      :global="tableGlobals"
      :has-linked-agencies="column.linkedStaffingAgencies.paginatorInfo.total > 0"
      :can-manage-agencies="column.viewerCanManageStaffingAgencies"
      :can-manage-owners="column.viewerCanManageOwners"
      @view-page="openClientPage"
      @link-agency="showLinkModal = true"
      @manage-owners="showOwnersModal = true"
    />

    <teleport :to="tableGlobals">
      <LinkRecruitersModal
        v-if="showLinkModal"
        :show="showLinkModal"
        mode="manage"
        :clients="[{ id: column.company.id, name: column.company.name }]"
        @update:show="showLinkModal = $event"
      />
      <ClientOwnersModal
        v-if="showOwnersModal"
        :show="showOwnersModal"
        :client-relation-id="column.id"
        @update:show="showOwnersModal = $event"
      />
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { FragmentType, graphql, useFragment } from '@/gql'
import ClientActionsMenu from '@/components/Clients/ClientActionsMenu.vue'
import LinkRecruitersModal from '@/components/Clients/LinkRecruitersModal.vue'
import ClientOwnersModal from '@/components/Owners/ClientOwnersModal.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const ClientActionColumn = graphql(`
  fragment ClientActionColumn on CompanySupplier {
    id
    viewerCanManageStaffingAgencies
    viewerCanManageOwners
    linkedStaffingAgencies(first: 100) {
      paginatorInfo {
        total
      }
    }
    company {
      id
      name
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof ClientActionColumn>
  rowIndex?: number
}>()

const emit = defineEmits<{ (e: 'open-page', companySupplierId: string): void }>()

const column = computed(() => useFragment(ClientActionColumn, props.row))
const rowIndex = computed(() => props.rowIndex ?? 0)

const showLinkModal = ref(false)
const showOwnersModal = ref(false)

const clientPageUrl = computed(() =>
  laravelNamedRoute(namedRoutes.frontend.clients.view, { companySupplier: column.value.id }),
)

// Same destination as the drawer's button: over the list; a menu option is no link, so modifier clicks get a new tab by hand.
function openClientPage(event: MouseEvent | undefined) {
  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
    window.open(clientPageUrl.value, '_blank', 'noopener')
    return
  }

  emit('open-page', column.value.id)
}
</script>

<style scoped lang="scss">
.client-action-column :deep(.wds-menu) {
  width: max-content;
}
</style>
